import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../services/api";
import { qlAlert, qlConfirm } from "../utils/dialogs";
import MathText from "./MathText";
import "./MesContenus.css";

/* Ajout direct de contenus pédagogiques par l'enseignant, dans ses matières.
   Le serveur vérifie la matière et l'auteur : seul l'auteur modifie ou supprime. */

const TYPES = [
  { cle: "lecon", label: "Leçon", pluriel: "Leçons", icone: "bi-file-text" },
  { cle: "video", label: "Vidéo", pluriel: "Vidéos", icone: "bi-play-circle" },
  { cle: "qcm", label: "QCM", pluriel: "QCM", icone: "bi-grid-3x3" },
  { cle: "exercice", label: "Exercice", pluriel: "Exercices", icone: "bi-pencil-square" },
];
const TYPES_PEDAGOGIQUES = [["cours", "Question de cours"], ["savoir-faire", "Savoir-faire"], ["competence", "Compétence"]];
const questionVide = () => ({ text: "", explication: "", reponses: [{ text: "", is_correct: true }, { text: "", is_correct: false }] });
const erreurApi = (err, defaut) => err?.response?.data?.detail || defaut;

function EditeurQuestions({ questions, onChange }) {
  const maj = (i, q) => onChange(questions.map((x, j) => (j === i ? q : x)));
  return (
    <div className="mc-questions">
      {questions.map((q, i) => (
        <fieldset key={i} className="mc-question">
          <legend>Question {i + 1}
            {questions.length > 1 && (
              <button type="button" className="mc-lien mc-lien--danger" onClick={() => onChange(questions.filter((_, j) => j !== i))}>
                <i className="bi bi-trash"></i> Retirer</button>
            )}
          </legend>
          <textarea rows={2} required placeholder="Énoncé de la question (LaTeX entre $…$ accepté)" value={q.text}
            onChange={(e) => maj(i, { ...q, text: e.target.value })} />
          {q.reponses.map((r, k) => (
            <div key={k} className="mc-reponse">
              <label className="mc-bonne" title="Bonne réponse">
                <input type="radio" name={`bonne-${i}`} checked={r.is_correct}
                  onChange={() => maj(i, { ...q, reponses: q.reponses.map((x, m) => ({ ...x, is_correct: m === k })) })} />
                <span>Bonne</span>
              </label>
              <input type="text" maxLength={300} placeholder={`Réponse ${k + 1}`} value={r.text}
                onChange={(e) => maj(i, { ...q, reponses: q.reponses.map((x, m) => (m === k ? { ...x, text: e.target.value } : x)) })} />
              {q.reponses.length > 2 && (
                <button type="button" className="mc-lien" aria-label="Retirer la réponse"
                  onClick={() => maj(i, { ...q, reponses: q.reponses.filter((_, m) => m !== k) })}><i className="bi bi-x-lg"></i></button>
              )}
            </div>
          ))}
          {q.reponses.length < 6 && (
            <button type="button" className="mc-lien" onClick={() => maj(i, { ...q, reponses: [...q.reponses, { text: "", is_correct: false }] })}>
              <i className="bi bi-plus-lg"></i> Ajouter une réponse</button>
          )}
          <textarea rows={2} placeholder="Explication affichée après correction (facultatif)" value={q.explication}
            onChange={(e) => maj(i, { ...q, explication: e.target.value })} />
        </fieldset>
      ))}
      <button type="button" className="ap-btn-secondary mc-ajout-q" onClick={() => onChange([...questions, questionVide()])}>
        <i className="bi bi-plus-circle me-1"></i>Ajouter une question</button>
    </div>
  );
}

function Formulaire({ type, modules, existant, onClose, onSaved }) {
  const [v, setV] = useState(() => ({
    module: existant?.module || modules[0]?.id || "", title: existant?.title || "", description: existant?.description || "",
    youtube_url: existant?.youtube_url || "", duration: existant?.duration || "", pedagogical_type: existant?.pedagogical_type || "cours",
    lecon: existant?.lecon || "", questions: existant?.questions?.length ? existant.questions : [questionVide()],
  }));
  const [fichier, setFichier] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const module = modules.find((m) => String(m.id) === String(v.module));
  const libelle = TYPES.find((t) => t.cle === type).label;

  const envoyer = (e) => {
    e.preventDefault();
    setErreur("");
    let corps;
    if (type === "qcm" && !fichier) {
      corps = { module: v.module, title: v.title, description: v.description, lecon: v.lecon || "", questions: v.questions };
    } else {
      corps = new FormData();
      const champs = { lecon: ["module", "title", "description"], video: ["module", "title", "description", "youtube_url", "duration"],
        exercice: ["module", "title", "description", "pedagogical_type"], qcm: ["module", "title", "description", "lecon"] }[type];
      champs.forEach((c) => corps.append(c, v[c] ?? ""));
      if (type === "qcm") corps.append("questions", JSON.stringify(v.questions));
      if (fichier) corps.append(type === "lecon" ? "pdf_lecon" : "image", fichier);
    }
    const options = corps instanceof FormData ? { headers: { "Content-Type": "multipart/form-data" } } : undefined;
    setEnvoi(true);
    const url = existant ? `enseignant/contenus/${type}/${existant.id}/` : `enseignant/contenus/${type}/`;
    (existant ? api.patch(url, corps, options) : api.post(url, corps, options))
      .then(() => { onSaved(existant ? `${libelle} modifié(e).` : `${libelle} publié(e) : vos élèves la voient déjà.`); })
      .catch((err) => setErreur(erreurApi(err, "Enregistrement impossible.")))
      .finally(() => setEnvoi(false));
  };

  return (
    <div className="mc-fond" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="mc-fenetre" onSubmit={envoyer} role="dialog" aria-modal="true" aria-label={`${existant ? "Modifier" : "Ajouter"} : ${libelle}`}>
        <div className="mc-fenetre-tete">
          <h3>{existant ? "Modifier" : "Ajouter"} : {libelle}</h3>
          <button type="button" className="mc-lien" onClick={onClose} aria-label="Fermer"><i className="bi bi-x-lg"></i></button>
        </div>
        <label>Module
          <select required value={v.module} onChange={(e) => setV({ ...v, module: e.target.value, lecon: "" })}>
            {modules.map((m) => <option key={m.id} value={m.id}>{m.manuel ? `${m.manuel} · ` : ""}{m.title} ({m.matiere})</option>)}
          </select>
        </label>
        <label>Titre
          <input type="text" required maxLength={200} value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} />
        </label>
        <label>{type === "exercice" ? "Énoncé (LaTeX entre $…$ accepté)" : "Description (facultatif)"}
          <textarea rows={type === "exercice" ? 6 : 3} value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} />
        </label>
        {type === "exercice" && v.description && <div className="mc-apercu"><MathText text={v.description} /></div>}
        {type === "lecon" && (
          <label>PDF de la leçon {existant && "(laisser vide pour garder l’actuel)"}
            <input type="file" accept="application/pdf" required={!existant} onChange={(e) => setFichier(e.target.files?.[0] || null)} />
          </label>
        )}
        {type === "video" && (
          <>
            <label>Lien de la vidéo (YouTube)
              <input type="url" required placeholder="https://youtu.be/…" value={v.youtube_url} onChange={(e) => setV({ ...v, youtube_url: e.target.value })} />
            </label>
            <label>Durée (facultatif)
              <input type="text" maxLength={50} placeholder="12 min" value={v.duration} onChange={(e) => setV({ ...v, duration: e.target.value })} />
            </label>
          </>
        )}
        {type === "exercice" && (
          <>
            <label>Type
              <select value={v.pedagogical_type} onChange={(e) => setV({ ...v, pedagogical_type: e.target.value })}>
                {TYPES_PEDAGOGIQUES.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
              </select>
            </label>
            <label>Image de l’exercice (facultatif)
              <input type="file" accept="image/*" onChange={(e) => setFichier(e.target.files?.[0] || null)} />
            </label>
          </>
        )}
        {type === "qcm" && (
          <>
            <label>Leçon rattachée (facultatif)
              <select value={v.lecon} onChange={(e) => setV({ ...v, lecon: e.target.value })}>
                <option value="">— Tout le module —</option>
                {(module?.lecons || []).map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
              </select>
            </label>
            <EditeurQuestions questions={v.questions} onChange={(questions) => setV({ ...v, questions })} />
          </>
        )}
        {erreur && <div className="alert alert-danger mb-0">{erreur}</div>}
        <div className="mc-actions">
          <button type="button" className="ap-btn-secondary" onClick={onClose}>Annuler</button>
          <button type="submit" className="ap-btn-primary" disabled={envoi}>
            {envoi ? "Envoi…" : existant ? "Enregistrer" : "Publier"}</button>
        </div>
      </form>
    </div>
  );
}

export default function MesContenus() {
  const [type, setType] = useState("lecon");
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState("");
  const [seulementMoi, setSeulementMoi] = useState(true);
  const [edition, setEdition] = useState(null);
  const [recherche, setRecherche] = useState("");

  const charger = useCallback(() => {
    api.get(`enseignant/contenus/${seulementMoi ? "?auteur=moi" : ""}`)
      .then((r) => { setDonnees(r.data); setErreur(""); })
      .catch((err) => setErreur(erreurApi(err, "Contenus indisponibles.")));
  }, [seulementMoi]);
  useEffect(() => { charger(); }, [charger]);

  const liste = useMemo(() => {
    const items = donnees?.contenus?.[type] || [];
    const q = recherche.trim().toLowerCase();
    return q ? items.filter((i) => `${i.title} ${i.module_title}`.toLowerCase().includes(q)) : items;
  }, [donnees, type, recherche]);

  const supprimer = (item) => qlConfirm(`Supprimer « ${item.title} » ? Les élèves ne le verront plus.`, () => {
    api.delete(`enseignant/contenus/${item.type}/${item.id}/`)
      .then(() => { qlAlert("Contenu supprimé.", "success"); charger(); })
      .catch((err) => qlAlert(erreurApi(err, "Suppression impossible."), "error"));
  }, { danger: true, okLabel: "Supprimer" });

  const t = TYPES.find((x) => x.cle === type);
  const modules = donnees?.modules || [];
  return (
    <div className="ap-page mc">
      <div className="ap-page-header">
        <h1 className="ap-page-title"><i className="bi bi-plus-square me-2" style={{ color: "#126ef4" }}></i>Ajouter des contenus</h1>
      </div>
      <p className="mc-intro">Publiez directement leçons, vidéos, QCM et exercices dans les modules de vos matières.
        Vous pouvez modifier ou supprimer uniquement ce que vous avez publié.</p>
      {erreur && <div className="alert alert-danger">{erreur}</div>}
      <div className="mc-onglets" role="tablist">
        {TYPES.map((x) => (
          <button key={x.cle} type="button" role="tab" aria-selected={type === x.cle}
            className={`mc-onglet${type === x.cle ? " actif" : ""}`} onClick={() => setType(x.cle)}>
            <i className={`bi ${x.icone}`}></i>{x.pluriel}
            <span className="mc-compte">{donnees?.contenus?.[x.cle]?.length ?? "…"}</span>
          </button>
        ))}
      </div>
      <div className="mc-barre">
        <input type="search" className="ap-search" placeholder={`Rechercher dans les ${t.pluriel.toLowerCase()}…`} value={recherche}
          onChange={(e) => setRecherche(e.target.value)} />
        <label className="mc-filtre"><input type="checkbox" checked={seulementMoi} onChange={(e) => setSeulementMoi(e.target.checked)} /> Mes publications seulement</label>
        <button type="button" className="ap-btn-primary" disabled={!modules.length} onClick={() => setEdition({ type })}>
          <i className="bi bi-plus-lg me-1"></i>Ajouter : {t.label}</button>
      </div>
      {donnees && !modules.length && (
        <div className="alert alert-warning">Aucune matière n’est associée à votre compte. Renseignez vos matières dans votre profil
          ou demandez-les à l’administration pour pouvoir publier.</div>
      )}
      {!donnees ? <div className="ap-loading"><div className="spinner-border text-primary"></div></div> : (
        <div className="mc-liste">
          {liste.map((item) => (
            <div key={item.id} className="mc-item">
              <i className={`bi ${t.icone} mc-item-icone`}></i>
              <div className="mc-item-texte">
                <div className="mc-item-titre">{item.title}</div>
                <div className="mc-item-meta">{item.manuel_title ? `${item.manuel_title} · ` : ""}{item.module_title} · {item.matiere}
                  {item.type === "qcm" && ` · ${item.questions.length} question(s)`}</div>
              </div>
              {item.modifiable ? (
                <div className="mc-item-actions">
                  <button type="button" className="mc-lien" onClick={() => setEdition({ type: item.type, existant: item })}><i className="bi bi-pencil"></i> Modifier</button>
                  <button type="button" className="mc-lien mc-lien--danger" onClick={() => supprimer(item)}><i className="bi bi-trash"></i> Supprimer</button>
                </div>
              ) : <span className="mc-badge">Officiel</span>}
            </div>
          ))}
          {!liste.length && <p className="text-muted py-3">Aucun contenu pour l’instant.</p>}
        </div>
      )}
      {edition && (
        <Formulaire type={edition.type} existant={edition.existant} modules={modules} onClose={() => setEdition(null)}
          onSaved={(msg) => { setEdition(null); qlAlert(msg, "success"); charger(); }} />
      )}
    </div>
  );
}
